import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../../api";
import type {
  BodyCreateOnboardingImportScopeType,
  EntityType,
  ImportView,
  JobStatus,
} from "../../generated/core-api";
import { onboardingJobHookError } from "../utils/backendErrorMessage";
import { isTerminalJobStatus, useOnboardingJobPoll } from "./useOnboardingJobPoll";

export interface InvokeOnboardingImportArgs {
  /** Workload to stage this import against — from `useOnboardingWorkload`. */
  workloadId: string;
  /** Raw uploaded file — server-side `SourceMappingWorkflow` does the CSV parse (LADR-039 W1). */
  file: File;
  entityType: EntityType;
  companyCode?: string;
  scopeType?: BodyCreateOnboardingImportScopeType;
  configCode?: string;
  configVersion?: number;
  providerId?: string;
  /** Optimistic-concurrency guard for re-uploading over a prior import. */
  ifMatchSourceVersion?: string;
}

export interface OnboardingImportState {
  invoke: (args: InvokeOnboardingImportArgs) => void;
  importId: string | null;
  status: JobStatus | null;
  /** Full read DTO from the latest poll; null until the first successful poll response. */
  data: ImportView | null;
  /** The start-import call failing. A terminal `status: "failed"` is surfaced via `status`/`data.outcome_summary`, not this field. */
  error: string | null;
  isInvoking: boolean;
  isPolling: boolean;
  reset: () => void;
}

/**
 * Start a multipart source-file import (`POST …/workloads/{workloadId}/imports`)
 * and poll it to terminal (`GET …/workloads/{workloadId}/imports/{importId}`) —
 * the LADR-039 Track 2 replacement for the Track 1 `invokeAgent` "ingest rows"
 * step (see `.plans/ladr-039-contract-mapping.md`'s shared shape reference:
 * client-parsed inline rows → multipart file ingest only).
 *
 * `BodyCreateOnboardingImport.file` is typed `string` by the orval-generated
 * client (a known generator quirk shared with the existing
 * `BodyImportPayComponents.file` — see `src/pay-component-import/AGENTS.md`);
 * at runtime `FormData.append` accepts a `File` directly, so the cast below
 * is safe. There is no `mode` field on this endpoint — validate/commit happen
 * against the resulting entity via separate `useOnboardingValidation`/
 * `useOnboardingMigrationRun` hooks (Step 5b/5c).
 */
export function useOnboardingImport(): OnboardingImportState {
  const [workloadId, setWorkloadId] = useState<string | null>(null);
  const [importId, setImportId] = useState<string | null>(null);

  const invokeMutation = useMutation({
    mutationFn: (args: InvokeOnboardingImportArgs) =>
      api.createOnboardingImport(args.workloadId, {
        file: args.file as unknown as string,
        entity_type: args.entityType,
        company_code: args.companyCode,
        scope_type: args.scopeType,
        config_code: args.configCode,
        config_version: args.configVersion,
        provider_id: args.providerId,
        if_match_source_version: args.ifMatchSourceVersion,
      }),
    onSuccess: (data, variables) => {
      setWorkloadId(variables.workloadId);
      setImportId(data.import_id);
    },
  });

  const pollQuery = useOnboardingJobPoll<ImportView>(
    ["onboardingImport", workloadId, importId],
    () => api.getOnboardingImport(workloadId as string, importId as string),
    !!workloadId && !!importId
  );

  const status = pollQuery.data?.status ?? null;

  const invoke = useCallback((args: InvokeOnboardingImportArgs) => {
    setImportId(null);
    invokeMutation.mutate(args);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = useCallback(() => {
    setWorkloadId(null);
    setImportId(null);
    invokeMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    invoke,
    importId,
    status,
    data: pollQuery.data ?? null,
    error: onboardingJobHookError(invokeMutation.error, pollQuery.error),
    isInvoking: invokeMutation.isPending,
    isPolling: !!importId && !isTerminalJobStatus(status) && !pollQuery.isError,
    reset,
  };
}
